<!-- Карточка статуса ЭДО-документа с компактным прогрессом по титулам. -->
<template>
  <section
    v-if="titles.length"
    class="edo-title-flow-card"
    :class="[
      `edo-title-flow-card--${variant}`,
      { 'edo-title-flow-card--expanded': expanded },
    ]"
    :aria-label="currentLabel"
  >
    <div class="edo-title-flow-card__head">
      <div class="edo-title-flow-card__heading">
        <span class="edo-title-flow-card__eyebrow">
          {{ eyebrow }}
        </span>
        <h4>{{ title }}</h4>
      </div>

      <BaseBadge
        v-if="currentLabel"
        class="edo-title-flow-card__status"
        :label="currentLabel"
        :variant="statusVariant"
      />
    </div>

    <div class="edo-title-flow-card__steps" :class="titleCountClass">
      <EdoDocumentTitleFlowStep
        v-for="item in titles"
        :key="item.key"
        :label="item.label"
        :note="item.note"
        :tone="item.tone"
        :selected="item.key === activeTitleKey"
        :aria-label="item.ariaLabel"
        :tooltip="item.tooltip"
        :disabled="readOnly && !inspectable"
        :compact="variant === 'embedded'"
        @select="$emit('select-title', item)"
      />
    </div>

    <div
      v-if="expanded"
      class="edo-title-flow-card__caret-grid"
      :class="titleCountClass"
      aria-hidden="true"
    >
      <span
        v-for="item in titles"
        :key="item.key"
        class="edo-title-flow-card__caret-cell"
      >
        <span
          v-if="item.key === activeTitleKey"
          class="edo-title-flow-card__caret"
        ></span>
      </span>
    </div>
  </section>
</template>

<script>
import EdoDocumentTitleFlowStep from "@/components/Edo/shared/EdoDocumentTitleFlowStep.vue";

export default {
  name: "EdoDocumentTitleFlowCard",

  emits: ["select-title"],

  components: {
    EdoDocumentTitleFlowStep,
  },

  props: {
    eyebrow: {
      type: String,
      default: "Документооборот",
    },
    title: {
      type: String,
      required: true,
    },
    currentLabel: {
      type: String,
      default: "",
    },
    tone: {
      type: String,
      default: "neutral",
      validator: (value) =>
        ["neutral", "success", "warning", "danger"].includes(value),
    },
    titles: {
      type: Array,
      default: () => [],
    },
    activeTitleKey: {
      type: String,
      default: null,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    inspectable: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "card",
      validator: (value) => ["card", "embedded", "flat"].includes(value),
    },
  },

  computed: {
    titleCountClass() {
      const count = Math.min(Math.max(this.titles.length, 1), 4);
      return `edo-title-flow-card__grid--${count}`;
    },

    statusVariant() {
      return this.tone === "neutral" ? "default" : this.tone;
    },
  },
};
</script>

<style scoped>
.edo-title-flow-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
  padding: 18px;
  border: 1px solid #e3e6ee;
  border-radius: 14px;
  background: #fff;
}

.edo-title-flow-card--expanded {
  border-radius: 16px 16px 0 0;
}

.edo-title-flow-card--embedded {
  gap: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.edo-title-flow-card--embedded.edo-title-flow-card--expanded {
  padding-bottom: 18px;
  border-bottom: 1px solid #e3e6ee;
}

.edo-title-flow-card--flat {
  padding-right: 0;
  padding-left: 0;
  border: 0;
  border-radius: 0;
}

.edo-title-flow-card--flat.edo-title-flow-card--expanded {
  border-bottom: 1px solid #e3e6ee;
}

.edo-title-flow-card--embedded .edo-title-flow-card__head {
  display: none;
}

.edo-title-flow-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.edo-title-flow-card__heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.edo-title-flow-card__eyebrow {
  color: #5b6070;
  font-size: 13px;
  line-height: 18px;
}

.edo-title-flow-card__heading h4 {
  margin: 0;
  color: var(--color-dark);
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
}

.edo-title-flow-card__status {
  flex: none;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.edo-title-flow-card__steps,
.edo-title-flow-card__caret-grid {
  display: grid;
  gap: 8px;
}

.edo-title-flow-card__grid--1 {
  grid-template-columns: minmax(0, 1fr);
}

.edo-title-flow-card__grid--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.edo-title-flow-card__grid--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.edo-title-flow-card__grid--4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.edo-title-flow-card__caret-grid {
  position: absolute;
  right: 18px;
  bottom: -8px;
  left: 18px;
  z-index: 2;
}

.edo-title-flow-card__caret-cell {
  display: flex;
  justify-content: center;
}

.edo-title-flow-card__caret {
  width: 14px;
  height: 14px;
  border-top: 1px solid #e3e6ee;
  border-left: 1px solid #e3e6ee;
  background: #fff;
  transform: rotate(45deg);
}

@media (max-width: 720px) {
  .edo-title-flow-card__head {
    flex-direction: column;
  }

  .edo-title-flow-card__status {
    white-space: normal;
  }

  .edo-title-flow-card__steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 16px;
  }

  .edo-title-flow-card__caret-grid {
    display: none;
  }
}

@media (max-width: 440px) {
  .edo-title-flow-card__steps {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
